import {
  useCallback,
  useEffect,
  useRef,
  useState } from "react";
import { loadManifest,
  loadRepoManifest,
  publishAssets,
  uploadAsset,
  type ManifestEntry } from "../lib/contentApi";
import { assembleStripBlob,
  fileToImage,
  loadImage,
  normalizeFrame,
  sliceStrip } from "../lib/strip";
import { gridToStrip,
  measureRef } from "../lib/gridStrip";
import { useRegisterSave } from "../lib/saveBar";
import { flipAssetEntry } from "../lib/mirrorImage";
import { draftSaveError } from "../lib/screenDraft";
import { assetFileName, saveAssetUrl, saveBlob } from "../lib/download";
import { GridHelp } from "./GridHelp";
import { ContextPreview } from "./ContextPreview";
import { StripPlay } from "./StripPlay";
import {
  isAnimSlotKey,
  animSlotLabel,
  isWalkStripKey,
  walkStripCharKey,
  WALK_DIRS,
  animSlotSuggestions,
  artFamilyOf,
  familyFrameSize,
  oddFrameSize,
  HERO_BASE_PRESETS,
  HERO_CATALOG,
  isLandingSlotKey,
  LOOP_FPS_MAX,
  repoArtOffer,
} from "@greg-chick/shared";

// 제안 목록은 카탈로그에서 전부 뽑는다 — 캐릭터를 늘려도 여기 손댈 필요 없다.
const SLOT_SUGGESTIONS = animSlotSuggestions({
  npcIds: ["greg_white", "chick", "greg_cat"],
  presetIds: HERO_BASE_PRESETS.map((p) => p.id),
  lookIds: HERO_CATALOG.body.flatMap((b) => HERO_CATALOG.outfit.map((o) => `${b}_${o}`)),
  loopKeys: ["landing.char.greg.strip", "landing.char.chick.strip", "decor.lantern.strip"],
});

// 애니메이션 매니저(FR-ADMIN 확장) — ① 게시된 애니 목록(미리보기·속도변경·편집·위치·제거)
// ② 새 애니 만들기(프레임 다중 업로드 → 스트립 자동 조립 → 미리보기 → 게시).
// 편집은 게시된 스트립을 프레임으로 되쪼개서(가로÷세로 슬라이스) 조립기로 불러온다 — 원본 프레임 보관 불필요.
// 규격: 정사각 N프레임 가로 스트립(웹 자동 판정, 공용 lib/strip). 걷기 스트립은 0번=서있기.

// 애니 슬롯 판정·이름표는 shared 단일 정의(isAnimSlotKey / animSlotLabel).
// ⚠️ 예전엔 여기 정규식이 박혀 있었고 npc.* / avatar.player.* 만 인정해서, 히어로 걷기 63장
//    (hero.base.*·hero.look.*)이 목록에서 통째로 빠져 운영자가 손댈 수 없었다. 웹은 재생하는데
//    편집기만 못 보던 것 — 규칙은 반드시 한 곳에.

interface Frame {
  id: number;
  name: string;
  img: HTMLImageElement;
}

/**
 * @param charKey 한 캐릭터의 움직임만 다루는 모드([🧸 캐릭터] 화면이 끼워 쓴다).
 *                이 캐릭터의 걷기·루프만 보여주고, 새로 만들 때 슬롯도 그 캐릭터 것으로 시작한다.
 * @param embedded 제목을 생략(밖에서 이미 제목을 그리고 있다).
 */
export function AnimationStudio({
  onGoZones,
  charKey,
  embedded,
}: {
  onGoZones?: () => void;
  charKey?: string;
  embedded?: boolean;
}) {
  // ── 게시된 애니 목록 ──
  const [assets, setAssets] = useState<Record<string, ManifestEntry>>({});
  /** 저장소(배포본)에 있는 같은 자리 — 「새로 다듬은 걷기로 바꾸기」가 무엇으로 바꿀지 본다. 못 읽었으면 빈 채(단추 안 뜸). */
  const [repo, setRepo] = useState<Record<string, ManifestEntry>>({});
  const [fpsDraft, setFpsDraft] = useState<Record<string, number>>({});
  const [posEdit, setPosEdit] = useState<string | null>(null); // 위치 편집 중인 부모 슬롯키
  const [posPending, setPosPending] = useState<ManifestEntry | null>(null);
  // ── 조립기 ──
  const [frames, setFrames] = useState<Frame[]>([]);
  const [slot, setSlot] = useState(charKey ? `${charKey}.down` : "npc.greg_white.down");
  const [fps, setFps] = useState(2);
  const [playIdx, setPlayIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  /** 이 자리가 첫 화면에서 계속 도는 그림인가 — 그러면 속도 상한이 다르다. */
  const landingLoop = isLandingSlotKey(slot);
  const [msg, setMsg] = useState<string | null>(null);
  const nextId = useRef(1);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const builderRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    const [m, r] = await Promise.all([loadManifest(), loadRepoManifest()]);
    setAssets(m.assets);
    setRepo(r?.assets ?? {});
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const animKeys = Object.keys(assets)
    .filter((k) => isAnimSlotKey(k) && assets[k]?.url)
    .filter((k) => !charKey || walkStripCharKey(k) === charKey || k === `${charKey}.strip`)
    .sort();

  /** 한 캐릭터 모드에서 고를 수 있는 슬롯 — 걷기 3방향(+럜딩 캐릭터는 제자리 루프). */
  const charSlots = charKey
    ? charKey.startsWith("landing.char.")
      ? [`${charKey}.strip`]
      : WALK_DIRS.map((d) => `${charKey}.${d}`)
    : [];

  /** 매니페스트 일부만 바꿔 게시(현재 게시본 기준 병합). */
  async function publishPatch(patch: (a: Record<string, ManifestEntry>) => void, okMsg: string) {
    setBusy(true);
    setMsg(null);
    try {
      const m = await loadManifest();
      const next = { ...m.assets };
      patch(next);
      const out = await publishAssets(next);
      setMsg(`✅ ${okMsg} (v${out.version})`);
      await refresh();
    } catch (e) {
      setMsg(`❌ 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ↔ **옆걸음을 좌우로 뒤집는다** (2026-09-13 운영자 신고 「탐험가1 옆으로 걷는 게 방향키 반대로 걷는다」).
   *
   * 규칙: 안 뒤집은 옆모습은 **오른쪽**을 본다 — 게임은 왼쪽으로 갈 때만 뒤집는다(`WorldScene` setFlipX).
   * 운영에 올린 탐험가 1 옆걸음(3칸)이 **왼쪽**을 보고 있어 오른쪽으로 가며 왼쪽을 봤다. 저장소 그림은 `pnpm art:facing`
   * 이 잠그지만 **관리자에서 올린 그림은 그 검사 밖**이고, 이 화면에는 뒤집는 길이 없었다.
   * · 뒤집는 일은 한 곳(`flipAssetEntry` — 칸마다 제자리, 순서 그대로. 통째로 뒤집으면 걸음이 뒤로 감긴다)
   * · 직전 그림을 `prev` 로 — [↩ 이전] 한 번에 되돌린다(두 번 뒤집어 되돌리면 재인코딩으로 뭉개진다)
   * · 🔴 같은 틱 두 번 누름을 ref 로 막는다 — 두 번 뒤집히면 **원래대로** 돌아가 고친 줄 알고 나간다
   */
  const flipping = useRef(false);
  async function flipSide(key: string) {
    if (flipping.current) return;
    flipping.current = true;
    setBusy(true);
    setMsg(`${animSlotLabel(key)} 를 좌우로 뒤집는 중…`);
    try {
      const cur = assets[key];
      if (!cur?.url) throw new Error("그림이 없어요");
      const next = await flipAssetEntry(key, cur.url, { loadImage, upload: uploadAsset });
      await publishPatch((a) => {
        a[key] = { ...a[key]!, ...next };
      }, `${animSlotLabel(key)} 를 좌우로 뒤집었어요 — 아이 화면에 바로 나가요. 틀렸으면 [↩ 이전]`);
    } catch (e) {
      setMsg(`⛔ 못 뒤집었어요: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      flipping.current = false;
      setBusy(false);
    }
  }

  /** ✏️ 편집: 스트립을 프레임으로 슬라이스해 조립기로. */
  /**
   * 🪶 **형제와 프레임 크기가 어긋난 띠 찾기** (판정은 shared `oddFrameSize`).
   *
   * ## 🔴 왜 필요한가 (2026-09-06)
   * 같은 걷기 띠를 만드는 길이 **둘**이다 — `scripts/slice-walk-sheet.cjs`(프레임 128px)와
   * 이 화면(`FRAME` 512px). 어느 쪽으로 만들었느냐에 따라 **8배**가 갈리는데 아무 경고가 없었다.
   * 운영에서 `hero.base.explorer_a.side` 만 512px 프레임이라 `/world` 첫 화면에서
   * 그 한 파일이 **695ms** 를 먹었다.
   *
   * 지금까지 유일한 해결책이 **「관리자에서 다시 올리기」**였는데, 그 그림은 **이미 서버에 있다.**
   * 다시 만들어 오라고 하면 아무도 안 한다(이 저장소가 이미 배운 것). 그래서 서버에 있는
   * 그것으로 **여기서** 형제 크기에 맞춰 다시 만든다.
   */
  const [frameSize, setFrameSize] = useState<Record<string, number>>({});
  useEffect(() => {
    let alive = true;
    for (const key of animKeys) {
      const url = assets[key]?.url;
      if (!url || frameSize[key]) continue;
      loadImage(url)
        .then((img) => {
          // 가로로 이어 붙인 정사각 프레임이라 **높이 = 프레임 한 변**이다
          if (alive && img.naturalHeight > 0) setFrameSize((p) => (p[key] ? p : { ...p, [key]: img.naturalHeight }));
        })
        .catch(() => undefined); // 못 재면 안 고치면 된다 — 잘못 재고 고치는 것보다 낫다
    }
    return () => {
      alive = false;
    };
  }, [animKeys, assets, frameSize]);

  /** 이 띠가 맞춰야 할 프레임 크기(px). 어긋나지 않았으면 `null`. */
  function fitTarget(key: string): number | null {
    const mine = frameSize[key];
    if (!mine) return null;
    const family = artFamilyOf(key);
    const siblings = animKeys
      .filter((k) => k !== key && artFamilyOf(k) === family && frameSize[k])
      .map((k) => frameSize[k]!);
    return oddFrameSize(mine, siblings);
  }

  /**
   * 🪶 **새로 굽는 띠의 칸 크기** — 이 자리의 형제들이 입을 모은 크기(shared `familyFrameSize`). 모르면 `undefined`(기본 512px).
   * 🔴 게시·저장·내보내기가 이걸 안 넘겨 512px 로 구웠다 — 128px 걷기를 편집해 올리자 8배로 부풀었다(2026-09-14 운영 점검).
   */
  function stripFrameFor(key: string): number | undefined {
    if (!key) return undefined;
    const family = artFamilyOf(key);
    const siblings = animKeys.filter((k) => k !== key && artFamilyOf(k) === family && frameSize[k]).map((k) => frameSize[k]!);
    return familyFrameSize(siblings) ?? undefined;
  }

  /** 서버에 있는 그 띠를 그대로 받아 **형제 크기로 다시 만들어** 올린다. */
  async function fitToSiblings(key: string, frame: number) {
    setBusy(true);
    setMsg(`${animSlotLabel(key)} 를 ${frame}px 프레임으로 다시 만드는 중…`);
    try {
      const imgs = await sliceStrip(assets[key]!.url);
      const blob = await assembleStripBlob(imgs, frame);
      const url = await uploadAsset(key, new File([blob], "strip.webp", { type: "image/webp" }));
      // 🔴 이전 주소를 `prev` 로 남긴다 — [↩ 이전] 로 되돌릴 수 있어야 한다
      await publishPatch((a) => {
        a[key] = { ...a[key]!, url, prev: assets[key]!.url };
      }, `${animSlotLabel(key)} 를 형제와 같은 ${frame}px 로 맞췄어요`);
      setFrameSize((p) => ({ ...p, [key]: frame }));
    } catch (e) {
      setMsg(`⛔ 못 맞췄어요: ${String(e).slice(0, 120)}`);
    } finally {
      setBusy(false);
    }
  }

  async function editStrip(key: string) {
    try {
      setMsg("스트립을 프레임으로 불러오는 중…");
      const imgs = await sliceStrip(assets[key]!.url);
      setFrames(imgs.map((img, i) => ({ id: nextId.current++, name: `프레임 ${i}`, img })));
      setSlot(key);
      setFps(assets[key]?.fps ?? 2);
      setMsg(`✏️ 편집 모드: ${key} 의 ${imgs.length}프레임을 불러왔어요. 수정 후 "스트립 게시"를 누르세요.`);
      builderRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (e) {
      setMsg(`❌ 불러오기 실패: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // ── 조립기 로직(미리보기 재생·프레임 조작) ──
  useEffect(() => {
    if (frames.length < 2) return;
    const t = window.setInterval(() => setPlayIdx((v) => (v + 1) % frames.length), 1000 / fps);
    return () => window.clearInterval(t);
  }, [frames.length, fps]);

  useEffect(() => {
    const cv = previewRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d")!;
    ctx.clearRect(0, 0, cv.width, cv.height);
    const f = frames[Math.min(playIdx, frames.length - 1)];
    if (f) ctx.drawImage(normalizeFrame(f.img), 0, 0, cv.width, cv.height);
  }, [frames, playIdx]);

  async function addFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("이미지 로드 실패"));
        img.src = url;
      }).catch(() => setMsg(`${file.name}: 이미지를 읽을 수 없어요`));
      if (img.naturalWidth > 0) {
        setFrames((p) => [...p, { id: nextId.current++, name: file.name, img }]);
      }
    }
  }

  /**
   * **2×2 격자 한 장**을 걷기 4프레임으로 푼다.
   *
   * 생성기(AI Studio·ChatGPT)는 가로로 긴 스트립을 잘 못 그려서 격자로 받는다
   * (docs/build/29). 지금까지는 개발자가 터미널에서만 풀 수 있었다.
   * 크기·발끝 계산은 `shared` 것을 쓴다 — 개발자 도구와 같은 숫자여야 한다.
   */
  async function addGrid(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const img = await fileToImage(file);
      // 기존 스트립이 있으면 그 키·발 위치에 맞춘다 — 없으면 그 방향만 키가 10% 작아진다
      const refUrl = isWalkStripKey(slot) ? assets[slot]?.url : undefined;
      const ref = refUrl ? await measureRef(refUrl) : null;
      // 칸 크기는 **형제 크기**로 — 작게 만들고 게시 때 늘리면 흐려진다(stripFrameFor 는 게시·저장과 같은 판정)
      const frame = stripFrameFor(slot);
      const r = await gridToStrip(img, { ...(ref ? { ref } : {}), lockHeight: true, ...(frame ? { frame } : {}) });

      // 만든 스트립을 다시 4프레임으로 풀어 아래 목록에 넣는다 —
      // 운영자가 순서를 바꾸거나 한 장을 빼는 기존 흐름을 그대로 쓰게.
      const imgs = await sliceStrip(r.dataUrl);
      setFrames(imgs.map((im, i) => ({ id: nextId.current++, name: `격자 ${i}`, img: im })));
      setMsg(
        `격자를 4프레임으로 풀었어요` +
          (ref ? ` · 기존 스트립 규격에 맞춤(몸 ${ref.bodyH}px)` : " · 기준 스트립이 없어 기본 규격") +
          (r.wobble > 0.04 ? ` · 칸마다 키가 ${Math.round(r.wobble * 100)}% 달라서 맞췄어요` : ""),
      );
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "격자를 풀지 못했어요");
    } finally {
      setBusy(false);
    }
  }

  function move(id: number, dir: -1 | 1) {
    setFrames((p) => {
      const i = p.findIndex((f) => f.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= p.length) return p;
      const n = [...p];
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });
  }

  /**
   * 게시. **왜 안 됐는지를 돌려준다**(성공이면 `null`).
   *
   * 🔴 예전엔 실패를 `setMsg` 로만 적고 삼켰다. 그 상태로 하단 저장바에 물리면
   * 바는 아무 일 없었던 듯 「✅ 웹에 올렸어요」라고 말한다 — 운영자는 안 올라간 것을
   * 올라간 줄 안다. 화면 문구는 그대로 두고, 부르는 쪽이 알 수 있게 값으로도 돌려준다.
   */
  async function publishStrip(): Promise<string | null> {
    if (frames.length < 2) {
      const why = "프레임을 2장 이상 올려주세요";
      setMsg(why);
      return why;
    }
    if (!slot.trim()) {
      const why = "슬롯 키를 입력해주세요";
      setMsg(why);
      return why;
    }
    setBusy(true);
    setMsg(null);
    try {
      const blob = await assembleStripBlob(frames.map((f) => f.img), stripFrameFor(slot.trim()));
      const url = await uploadAsset(slot.trim(), new File([blob], "strip.webp", { type: "image/webp" }));
      const m = await loadManifest();
      const old = m.assets[slot.trim()];
      const next = {
        ...m.assets,
        [slot.trim()]: {
          ...(old ?? {}),
          url,
          fps,
          ...(old?.url && old.url !== url ? { prev: old.url } : {}), // 직전 버전 보존(1클릭 롤백)
        },
      };
      const out = await publishAssets(next);
      setMsg(`✅ 게시 완료 (v${out.version}) — ${frames.length}프레임, ${fps}fps → ${slot.trim()}`);
      await refresh();
      return null;
    } catch (e) {
      const why = e instanceof Error ? e.message : String(e);
      setMsg(`❌ 실패: ${why}`);
      return why;
    } finally {
      setBusy(false);
    }
  }

  /**
   * 💾 **저장** — 담아 둔 그림을 잃지 않게. 아이 화면은 **그대로 둔다**(게시가 아니다).
   *
   * ## 🔴 왜 그냥 브라우저에 못 담나
   * 여기 담긴 그림은 `blob:`·`data:` 주소로 **이 창 안에만** 있다. 창을 닫으면 사라지고,
   * 보관함은 `data:` 를 아예 뺀다(한 장에 수 MB — 저장소가 한 번에 찬다).
   * 그래서 저장할 때 **띠로 조립해 서버에 올려 두고**(게시본은 안 건드린다) 주소만 보관한다.
   * 올린 파일 이름은 내용 해시라 지금 아이가 보는 그림을 덮지 않는다 — 게시해야 바뀐다.
   *
   * 그러면 [불러오기] 한 번으로 다음 날에도, 다른 창에서도 이어서 만들 수 있다
   * (이미 있는 띠를 되쪼개는 [✏️ 편집]과 같은 길이다).
   */
  const savedUrl = useRef<string | null>(null);
  const draftId = charKey ? `anim.${charKey}` : "anim";
  const draftData = () => ({
    slot: slot.trim(),
    fps,
    n: frames.length,
    ...(savedUrl.current ? { url: savedUrl.current } : {}),
  });

  async function saveWork(): Promise<void> {
    // 자리를 모르면 올릴 데가 없다. 「저장 안 됨」만 뜨는 것보다 무엇을 하면 되는지 말한다.
    if (!slot.trim()) throw new Error("어느 자리에 올릴지 먼저 고른 뒤 저장해 주세요");
    if (frames.length > 0) {
      const blob = await assembleStripBlob(frames.map((f) => f.img), stripFrameFor(slot.trim()));
      savedUrl.current = await uploadAsset(slot.trim(), new File([blob], "strip.webp", { type: "image/webp" }));
    }
    const why = draftSaveError(draftId, draftData());
    if (why) throw new Error(why); // 삼키지 않는다 — 저장바가 **왜** 안 됐는지 적는다
    setMsg(
      frames.length > 0
        ? `💾 ${frames.length}장을 저장해 뒀어요 — 아이 화면은 그대로예요(올리려면 [스트립 게시]).`
        : "💾 자리·속도만 저장해 뒀어요(담긴 그림이 없어요).",
    );
  }

  /** 저장해 둔 것을 화면에 되돌린다 — 띠를 다시 프레임으로 쪼갠다. */
  function restoreWork(d: unknown): void {
    const v = (d ?? {}) as { slot?: string; fps?: number; url?: string };
    // 🔴 한 캐릭터만 보는 화면에서는 **그 캐릭터의 자리**만 받는다. 다른 자리를 그대로 넣으면
    //    라디오 어느 것도 안 켜진 채로 남고, 게시가 엉뚱한 자리로 간다.
    if (typeof v.slot === "string" && v.slot && (!charKey || charSlots.includes(v.slot))) setSlot(v.slot);
    if (typeof v.fps === "number") setFps(v.fps);
    if (!v.url) {
      setMsg("💾 저장해 둔 자리·속도만 불러왔어요(담긴 그림은 없었어요).");
      return;
    }
    savedUrl.current = v.url;
    setMsg("저장해 둔 움직임을 불러오는 중…");
    sliceStrip(v.url)
      .then((imgs) => {
        setFrames(imgs.map((img, i) => ({ id: nextId.current++, name: `프레임 ${i}`, img })));
        setMsg(`💾 저장해 둔 ${imgs.length}장을 불러왔어요. 이어서 고치고 [스트립 게시]를 누르세요.`);
      })
      .catch(() => setMsg("⚠️ 저장해 둔 그림을 못 불러왔어요 — 서버에서 지워졌을 수 있어요."));
  }

  /**
   * ⬇ **내보내기** — 지금 담은 것을 띠 그림 파일 한 장으로 내 컴퓨터에.
   *
   * 게시하지 않고도 결과물을 손에 쥘 수 있어야 한다(다른 사람에게 넘기기·보관·다시 올리기).
   * 이름은 **자리 이름**을 쓴다 — 폴더에 서른 장이 쌓이면 어느 칸이었는지 아무도 모른다
   * (규칙은 받기 한 곳 `lib/download` 에).
   */
  async function exportWork(): Promise<void> {
    if (frames.length === 0) return;
    setBusy(true);
    try {
      const blob = await assembleStripBlob(frames.map((f) => f.img), stripFrameFor(slot.trim()));
      saveBlob(blob, assetFileName(slot.trim() || "anim", "strip.webp", blob.type));
      setMsg(`⬇ ${frames.length}장을 파일 한 장으로 받았어요 (${fps}fps 로 재생돼요).`);
    } catch (e) {
      setMsg(`⛔ 못 내보냈어요: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 저장은 **화면 아래 고정 바 하나**로 모은다(관리자 공통 규칙) — 패널마다 저장 버튼을
   * 두면 "어느 걸 눌러야 하지"가 된다. 여기 등록하면 [💾 저장하기]·[🚀 웹에 올리기]와
   * 「저장해 둔 것이 있어요」 안내가 한 자리에서 붙고, 창을 닫으려 할 때 경고도 붙는다.
   */
  useRegisterSave({
    id: draftId,
    label: charKey ? "이 친구 움직임" : "움직임 만들기",
    dirty: frames.length >= 2 ? 1 : 0,
    save: async () => {
      const why = await publishStrip();
      if (why) throw new Error(why); // 안 올라갔는데 「올렸어요」라고 말하지 않게
    },
    saveDraft: saveWork,
    draft: { data: draftData, restore: restoreWork },
  });

  // 위치 편집 대상: landing.char.<id>.strip → 부모 landing.char.<id>(무대 pos 권위)
  const posParent = posEdit?.replace(/\.strip$/, "") ?? null;

  return (
    <section className="as-sec anim-studio">
      {embedded ? null : <h2>🎞 애니메이션</h2>}

      {/* ── ① 게시된 애니메이션(관리) ── */}
      <h3 className="anim-h3">{charKey ? "지금 들어 있는 움직임" : "게시된 애니메이션"}</h3>
      {animKeys.length === 0 ? (
        <p className="hint">
          {charKey
            ? "이 친구는 아직 움직임이 없어요. 아래에서 그림 여러 장을 올려 만들어 보세요."
            : "아직 게시된 애니메이션이 없어요. 아래에서 새로 만들어 보세요."}
        </p>
      ) : (
        <div className="anim-cards">
          {animKeys.map((key) => {
            const e = assets[key]!;
            const curFps = e.fps ?? (isWalkStripKey(key) ? 8 : 2); // 걷기는 8, 제자리 루프는 2(시각안전)
            const isLanding = key.startsWith("landing.char.") && key.endsWith(".strip");
            return (
              <div key={key} className="anim-card">
                <StripPlay url={e.url} fps={curFps} size={64} />
                {/* ↔ 옆걸음은 가는 쪽을 봐야 한다 — 오른쪽으로 갈 때(그대로)와 왼쪽으로 갈 때(게임이 뒤집은 모습)를 나란히 */}
                {key.endsWith(".side") ? (
                  <div className="anim-face" data-facing-check>
                    <figure>
                      <StripPlay url={e.url} fps={curFps} size={36} />
                      <figcaption>→ 오른쪽</figcaption>
                    </figure>
                    <figure>
                      <div className="anim-face__flip">
                        <StripPlay url={e.url} fps={curFps} size={36} />
                      </div>
                      <figcaption>← 왼쪽</figcaption>
                    </figure>
                    <span className="hint">가는 쪽을 보고 있나요?</span>
                  </div>
                ) : null}
                <div className="anim-card__meta">
                  <b className="anim-card__name">{animSlotLabel(key)}</b>
                  <code className="anim-card__key">{key}</code>
                  <span className="hint">{curFps}fps</span>
                </div>
                <div className="anim-card__ops">
                  <label>
                    ⚡
                    <input
                      type="number"
                      min={0.2}
                      max={12}
                      step={0.1}
                      value={fpsDraft[key] ?? curFps}
                      onChange={(ev) => setFpsDraft((p) => ({ ...p, [key]: Number(ev.target.value) }))}
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void publishPatch((a) => {
                          a[key] = { ...a[key]!, fps: Math.max(0.2, Math.min(12, fpsDraft[key] ?? curFps)) };
                        }, `${key} 속도 변경`)
                      }
                    >
                      적용
                    </button>
                  </label>
                  <button type="button" disabled={busy} onClick={() => void editStrip(key)}>
                    ✏️ 편집
                  </button>
                  {/* ⬇ 게시된 것도 파일로 꺼낼 수 있어야 한다 — 다른 사람에게 넘기거나 보관할 때.
                      다른 출처(에셋 서버)라 그냥 링크로는 저장이 안 된다(lib/download 참고). */}
                  <button
                    type="button"
                    disabled={busy}
                    title="이 움직임을 그림 파일 한 장으로 내 컴퓨터에 저장해요"
                    onClick={() =>
                      void saveAssetUrl(key, e.url).catch((err: unknown) =>
                        setMsg(`⛔ 못 받았어요: ${err instanceof Error ? err.message : String(err)}`),
                      )
                    }
                  >
                    ⬇ 내보내기
                  </button>
                  {/**
                   * 🖼️ **새로 다듬은 걷기로 바꾸기** (2026-09-13 · 탐험가 1 옆걸음이 부자연스럽다는 신고).
                   * 운영자가 올린 걷기(`/assets/…`)가 게시돼 있으면 저장소에 새로 만든 걷기(`/art/…`)가 **가려져 영영 안 보인다** —
                   * [🗑 제거]는 「지움」 표시까지 보내서 저장소 그림도 안 살아난다. 그래서 여기서 **골라서** 바꾼다.
                   * 판정은 shared `repoArtOffer`(마법 층의 같은 단추와 한 규칙) · 속도도 저장소 것으로 · 직전 그림은 prev → [↩ 이전].
                   */}
                  {repoArtOffer(e, repo[key]) ? (
                    <button
                      type="button"
                      disabled={busy}
                      title="새로 다듬어 둔 걷기로 바꿔요 — 아이 화면에 바로 나가고, 마음에 안 들면 [↩ 이전]"
                      onClick={() =>
                        void publishPatch((a) => {
                          const next = repoArtOffer(a[key], repo[key]);
                          if (!next) throw new Error("바꿀 새 그림이 없어요 — 화면을 새로고침해 주세요");
                          a[key] = { ...next, prev: a[key]!.url };
                        }, `${animSlotLabel(key)} 를 새로 다듬은 걷기로 바꿨어요 — 틀렸으면 [↩ 이전]`)
                      }
                    >
                      🖼️ 새로 다듬은 걷기로 바꾸기
                    </button>
                  ) : null}
                  {key.endsWith(".side") ? (
                    <button
                      type="button"
                      disabled={busy}
                      title="옆걸음은 오른쪽을 봐야 해요 — 방향키 반대로 걸으면 눌러 주세요(칸마다 제자리에서 뒤집어요)"
                      onClick={() => void flipSide(key)}
                    >
                      ↔ 좌우 뒤집기
                    </button>
                  ) : null}
                  {/* 🪶 형제와 크기가 어긋난 띠 — 서버에 있는 그림으로 여기서 맞춘다(다시 올릴 필요 없음) */}
                  {fitTarget(key) ? (
                    <button
                      type="button"
                      disabled={busy}
                      title={`지금 ${frameSize[key]}px · 형제는 ${fitTarget(key)}px — 아이 화면이 그만큼 빨라져요`}
                      onClick={() => void fitToSiblings(key, fitTarget(key)!)}
                    >
                      📐 형제와 같은 크기로
                    </button>
                  ) : null}
                  {e.prev ? (
                    <button
                      type="button"
                      disabled={busy}
                      title="직전 버전으로 되돌리기"
                      onClick={() =>
                        void publishPatch((a) => {
                          a[key] = { ...a[key]!, url: e.prev!, prev: e.url };
                        }, `${key} 이전 버전으로`)
                      }
                    >
                      ↩ 이전
                    </button>
                  ) : null}
                  {isLanding ? (
                    <button type="button" onClick={() => { setPosEdit(key); setPosPending(null); }}>
                      📍 위치
                    </button>
                  ) : (
                    <button type="button" onClick={onGoZones} title="월드 위치는 존 배치 편집에서 조정">
                      🗺️ 체험공간 배치로
                    </button>
                  )}
                  <button
                    type="button"
                    className="danger"
                    disabled={busy}
                    onClick={() => {
                      if (!confirm(`${key} 애니메이션을 제거할까요?\n(정지 이미지/기본 모습으로 돌아갑니다)`)) return;
                      void publishPatch((a) => {
                        delete a[key];
                      }, `${key} 제거`);
                    }}
                  >
                    🗑 제거
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 위치 편집 모달(랜딩 캐릭터 — 부모 슬롯 pos 드래그, 기존 위치 미리보기 재사용) */}
      {posEdit && posParent ? (
        <div className="anim-pos-backdrop" onClick={(ev) => ev.target === ev.currentTarget && setPosEdit(null)}>
          <div className="anim-pos">
            <h3>📍 위치 조정 — {posParent}</h3>
            <p className="hint">무대 위 캐릭터를 드래그해 위치를 정하고 저장하세요(스트립은 이 위치에서 재생됩니다).</p>
            <ContextPreview
              slotKey={posParent}
              // 🔴 비운 자리(`{ url: "" }`)가 폴백을 가리지 않게 `||`
              url={assets[posParent]?.url || assets[posEdit]!.url}
              entry={posPending ?? assets[posParent] ?? { url: assets[posEdit]!.url }}
              onChange={(_k, entry) => setPosPending(entry)}
            />
            <div className="anim-pos__actions">
              <button type="button" onClick={() => setPosEdit(null)}>취소</button>
              <button
                type="button"
                className="primary"
                disabled={busy || !posPending}
                onClick={() =>
                  void publishPatch((a) => {
                    a[posParent] = { ...(a[posParent] ?? { url: assets[posEdit!]!.url }), ...posPending! };
                  }, `${posParent} 위치 저장`).then(() => setPosEdit(null))
                }
              >
                위치 저장
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── ② 새 애니메이션 만들기(조립기) ── */}
      <div ref={builderRef}>
        <h3 className="anim-h3">새 애니메이션 만들기</h3>
        <p className="hint">
          프레임 이미지(투명 PNG/WebP)를 순서대로 올리면 자동으로 스트립으로 조립해 게시합니다. 걷기 스트립은{" "}
          <b>0번=서있기</b>. 루프는 [기본, 변형1, 기본, 변형2]처럼 기본 프레임을 사이에 넣으면 자연스러워요.
        </p>

        {/* AI 로 뽑은 격자 한 장을 그대로 받는다 — 운영자가 손으로 자를 방법이 없었다 */}
        <div className="anim-grid-in">
          <label className="anim-grid-in__btn">
            🧩 2×2 격자 한 장으로 만들기
            <input type="file" accept="image/*" hidden onChange={(e) => void addGrid(e.target.files)} />
          </label>
          <span className="hint">
            AI 에게 「2×2 격자에 걷기 4단계」로 받은 그림을 올리면 배경 제거·키 맞춤·발끝 정렬까지 한 번에 해요
          </span>
        </div>
        {/* 처음 보는 사람은 「2×2 격자」가 뭔지 모른다 — 화면 안에서 끝나게 한다 */}
        <GridHelp />

        <p className="hint">또는 프레임을 한 장씩:</p>
        <input type="file" accept="image/*" multiple onChange={(e) => void addFiles(e.target.files)} />

        {frames.length > 0 ? (
          <div className="anim-frames">
            {frames.map((f, i) => (
              <div key={f.id} className="anim-frame">
                <img src={f.img.src} alt={f.name} />
                <div className="anim-frame__ops">
                  <span>#{i}</span>
                  <button type="button" onClick={() => move(f.id, -1)} aria-label="앞으로">←</button>
                  <button type="button" onClick={() => move(f.id, 1)} aria-label="뒤로">→</button>
                  <button type="button" onClick={() => setFrames((p) => p.filter((x) => x.id !== f.id))} aria-label="삭제">✕</button>
                </div>
              </div>
            ))}
            <button type="button" className="anim-clear" onClick={() => setFrames([])}>전체 비우기</button>
          </div>
        ) : null}

        {landingLoop ? (
          <p className="hint">
            🐣 첫 화면에서 <b>계속 도는</b> 그림이라 <b>{LOOP_FPS_MAX}fps 까지</b>만 돼요
            (눈이 편하도록). 더 빠르게 두면 아이 화면에서 어차피 이 속도로 나와요.
          </p>
        ) : null}

        {frames.length >= 2 ? (
          <div className="anim-preview">
            <canvas ref={previewRef} width={160} height={160} />
            <label>
              속도 {fps}fps
              {/**
               * 🔴 **첫 화면에서 계속 도는 그림은 3fps 까지다**(시각 안전, 불변식 6).
               * 예전엔 여기서 8까지 밀 수 있었고, 아이 화면이 조용히 3으로 낮췄다 —
               * **미리보기는 6인데 실제는 3.** 운영자는 왜 느린지 알 수가 없다.
               * 이제 여기서 막고, 왜 막는지도 적는다.
               */}
              <input
                type="range"
                min={0.5}
                max={landingLoop ? LOOP_FPS_MAX : 8}
                step={0.5}
                value={fps}
                onChange={(e) => setFps(Number(e.target.value))}
              />
            </label>
            {fps > 3 ? <p className="hint">⚠️ 랜딩·장식 루프는 3fps 이하 권장(시각안전) — 걷기 스트립은 8까지 OK</p> : null}
          </div>
        ) : null}

        <div className="anim-slot">
          {charKey ? (
            // 한 캐릭터를 보고 있을 땐 슬롯 키를 손으로 치게 하지 않는다 — 한 글자 틀리면 엉뚜한 자리에 올라간다.
            <div className="anim-slot__pick" role="radiogroup" aria-label="어느 움직임으로 올릴까요">
              {charSlots.map((k) => (
                <label key={k} className="anim-slot__opt" data-on={slot === k || undefined}>
                  <input type="radio" name="anim-slot" checked={slot === k} onChange={() => setSlot(k)} />
                  {animSlotLabel(k).split(" · ").pop()}
                </label>
              ))}
            </div>
          ) : (
            <label>
              게시 슬롯
              <input list="anim-slot-list" value={slot} onChange={(e) => setSlot(e.target.value)} />
              <datalist id="anim-slot-list">
                {SLOT_SUGGESTIONS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </datalist>
            </label>
          )}
          <button
            type="button"
            className="primary"
            disabled={busy || frames.length < 2}
            // 🔴 그림이 한 장이면 회색인데 이유가 없었다. 움직임은 두 장부터 만들어진다.
            title={frames.length < 2 ? "그림이 2장 이상이어야 움직여요. 아래에서 더 담아 주세요." : undefined}
            onClick={() => void publishStrip()}
          >
            {busy ? "게시 중…" : "스트립 게시 →"}
          </button>
          {/* ⬇ 게시하지 않고도 결과물을 손에 쥘 수 있게. 한 장만 담아도 받을 수 있다
              (움직이지는 않지만 규격에 맞춰 다듬은 그림이라 쓸모가 있다). */}
          {frames.length > 0 ? (
            <button type="button" className="btn" disabled={busy} onClick={() => void exportWork()}>
              ⬇ 내보내기
            </button>
          ) : null}
          {frames.length < 2 && !busy ? (
            <p className="hint">그림이 <b>2장 이상</b>이어야 움직여요. 지금 {frames.length}장 담겼어요.</p>
          ) : null}
          {/* 💾 저장 버튼은 화면 **아래 고정 바**에 있다(관리자 공통). 어디 있는지 안 적으면
              운영자는 이 화면에 저장하는 길이 [스트립 게시]뿐인 줄 안다 — 그건 곧 미완성 게시다. */}
          <p className="hint">
            💾 아직 다 못 만들었으면 화면 <b>아래 [💾 저장하기]</b> — 담은 그림을 보관해 두고
            다음에 [불러오기]로 이어서 만들어요. <b>아이 화면은 그대로예요</b>(게시가 아니에요).
          </p>
        </div>
      </div>

      {msg ? <p className="hint anim-msg">{msg}</p> : null}
    </section>
  );
}
